/**
 * Which machines a phase runs on, and what state they are in.
 *
 * Asked for 2026-10-29: *"la phases, sub progress bar scrie numele de faza. ar
 * fi frumos sa le facem mai colorate, si sa trecem si masinile care au fost
 * executate sau sunt executate in curs, ai grija pot fi mai multe masini
 * odata."*
 *
 * The warning in that sentence is the whole design constraint. A phase is NOT
 * one machine:
 *
 *  · four insulation runs can sit on l75 while a fifth is on l70;
 *  · a rope can be stranded on skip2250 and ath600 in the same lot;
 *  · and a factory-synced job has tasks with no machine at all.
 *
 * So this answers with a LIST, de-duplicated by machine, each entry carrying how
 * many runs are on it and the strongest state among them. One chip per machine,
 * not one per run — a phase with four l75 runs reads "L75 ×4", not "L75 L75 L75
 * L75".
 */

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const DONE = new Set(['COMPLETED', 'DONE', 'EXECUTED', 'FINISHED']);
const RUNNING = new Set(['ACTIVE', 'RUNNING', 'IN_PROGRESS']);

/**
 * Three states, because three is what a planner acts on differently.
 *
 * `running` — go and look at it now. `done` — it happened, and on which line.
 * `queued` — it is promised to that machine but has not started.
 */
export function machineStateOf(status) {
  const s = String(status || '').toUpperCase();
  if (RUNNING.has(s)) return 'running';
  if (DONE.has(s)) return 'done';
  return 'queued';
}

/** The strongest of two states. Running beats done beats queued. */
const strongest = (a, b) => {
  const rank = { queued: 0, done: 1, running: 2 };
  return rank[b] > rank[a] ? b : a;
};

/** How a machine id is written on screen, everywhere in this app. */
export const machineLabel = id => String(id || '').toUpperCase();

/**
 * The machines of one phase of a job.
 *
 * Reads `job.tasks` — the raw job document, which is what `/api/redis/jobs`
 * hands out whole. Returns `[]` rather than null for a phase nobody has
 * scheduled, so a caller can map over it without a guard.
 *
 * @returns {Array<{machineId, label, state, count, plannedLength}>}
 */
export function phaseMachines(job, phaseKey) {
  if (!job || !phaseKey) return [];
  const byMachine = new Map();

  for (const t of Object.values(job.tasks || {})) {
    if (!t || String(t.phase || '') !== String(phaseKey)) continue;
    if (!t.machineId) continue; // a factory-synced task names no line

    const key = String(t.machineId);
    const state = machineStateOf(t.status);
    const prev = byMachine.get(key);

    byMachine.set(key, {
      machineId: key,
      label: machineLabel(key),
      state: prev ? strongest(prev.state, state) : state,
      count: (prev?.count ?? 0) + 1,
      plannedLength: Math.round(((prev?.plannedLength ?? 0) + num(t.plannedLength)) * 100) / 100
    });
  }

  // Running first — that is the one somebody is looking for — then the lines
  // that have already done their part, then what is merely promised. Machines
  // in the same state keep a stable alphabetical order so the chips do not
  // reshuffle between renders.
  const rank = { running: 0, done: 1, queued: 2 };
  return [...byMachine.values()].sort(
    (a, b) => rank[a.state] - rank[b.state] || a.label.localeCompare(b.label)
  );
}

/**
 * The same shape, from the pre-chewed list the bobin-state endpoint sends.
 *
 * Project Token State does not get whole job documents — `controller/bobins.js`
 * reshapes them onto rows — so the machines arrive as
 * `[{machineId, running, count, plannedLength}]` and only need normalising.
 * Kept here next to `phaseMachines` so both screens render from one shape.
 */
export function normalizeMachines(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .filter(m => m && m.machineId)
    .map(m => ({
      machineId: String(m.machineId),
      label: machineLabel(m.machineId),
      state: m.running ? 'running' : machineStateOf(m.status),
      count: num(m.count) || 1,
      plannedLength: num(m.plannedLength),
      phase: m.phase ?? null
    }))
    .sort((a, b) => (a.state === 'running' ? -1 : 0) - (b.state === 'running' ? -1 : 0));
}

/**
 * Bootstrap colour per state.
 *
 * Only three, and they mean the same thing on every screen that draws a machine
 * chip. Deliberately NOT the phase palette: a chip says where the work is, the
 * phase label says what the work is, and colouring them from one scale would
 * make a green chip look like a finished phase.
 */
export const MACHINE_STATE_BG = {
  running: 'success',
  done: 'secondary',
  queued: 'info'
};

/** What the chip's tooltip says. */
export function machineTitle(m, { phaseLabel } = {}) {
  if (!m) return '';
  const where = phaseLabel ? ` on ${phaseLabel}` : '';
  const runs = m.count > 1 ? `${m.count} runs` : '1 run';
  const metres =
    m.plannedLength > 0 ? ` · ${m.plannedLength.toLocaleString('ro-RO')} m planned` : '';

  if (m.state === 'running') return `${m.label} is running${where} — ${runs}${metres}`;
  if (m.state === 'done') return `${m.label} has finished${where} — ${runs}${metres}`;
  return `${runs}${where} queued on ${m.label}${metres}`;
}

export default {
  machineStateOf,
  machineLabel,
  phaseMachines,
  normalizeMachines,
  MACHINE_STATE_BG,
  machineTitle
};
